import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, Image as ImageIcon, X, Loader2 } from 'lucide-react';
import { compressImage, validateImageFile } from '../../../utils/imageCompression';
import { useToast } from '../../../hooks/useToast';
import { usePresignDeliveryUploadMutation } from '../../../store/slices/apiSlice';
import { DeliveryPhoto } from './DeliveryPhoto';

interface PhotoUploaderProps {
  jobId: string;
  photos: string[];
  onPhotosChange: (photos: string[]) => void;
  maxPhotos?: number;
  onUploadingChange?: (uploading: boolean) => void;
}

interface UploadingFile {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

export const PhotoUploader: React.FC<PhotoUploaderProps> = ({
  jobId,
  photos,
  onPhotosChange,
  maxPhotos = 5,
  onUploadingChange,
}) => {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isUploading = uploadingFiles.some(f => f.status === 'uploading' || f.status === 'pending');

  useEffect(() => {
    onUploadingChange?.(isUploading);
  }, [isUploading, onUploadingChange]);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const [presignUpload] = usePresignDeliveryUploadMutation();

  const uploadToS3 = async (file: File, uploadId: string): Promise<string | null> => {
    try {
      // 1. Get presigned POST data from backend
      const { url, fields, public_url } = await presignUpload({
        job_id: jobId,
        file_type: file.type,
      }).unwrap();

      // 2. Create form data with presigned fields
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append('file', file);

      // 3. POST file to S3
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`S3 upload failed: ${response.status} - ${text}`);
      }

      return public_url;
    } catch (error) {
      console.error('Upload error:', error);
      return null;
    }
  };

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const remainingSlots = maxPhotos - photos.length;
    if (remainingSlots <= 0) {
      toast.error('Maximum photos reached', `You can only upload ${maxPhotos} photos`);
      return;
    }

    const filesToProcess = Array.from(files).slice(0, remainingSlots);

    // Create upload entries
    const newUploads: UploadingFile[] = filesToProcess.map((file, index) => ({
      id: `${Date.now()}-${index}`,
      file,
      status: 'pending',
    }));

    setUploadingFiles(newUploads);

    // Process each file
    const uploadedUrls: string[] = [];

    for (let i = 0; i < newUploads.length; i++) {
      const upload = newUploads[i];
      
      // Update status to uploading
      setUploadingFiles(prev => prev.map(u => 
        u.id === upload.id ? { ...u, status: 'uploading' } : u
      ));

      try {
        // Validate file
        const validation = validateImageFile(upload.file);
        if (!validation.valid) {
          throw new Error(validation.error);
        }

        // Compress image
        const compressed = await compressImage(upload.file, { maxSizeKB: 500 });
        
        // Convert blob to file
        const compressedFile = new File([compressed.blob], upload.file.name, {
          type: compressed.blob.type,
          lastModified: upload.file.lastModified,
        });

        // Upload to S3
        const s3Url = await uploadToS3(compressedFile, upload.id);
        
        if (!s3Url) {
          throw new Error('Failed to upload to S3');
        }

        uploadedUrls.push(s3Url);

        // Update status to done
        setUploadingFiles(prev => prev.map(u => 
          u.id === upload.id ? { ...u, status: 'done' } : u
        ));

      } catch (error) {
        console.error('Processing error:', error);
        setUploadingFiles(prev => prev.map(u => 
          u.id === upload.id ? { 
            ...u, 
            status: 'error', 
            error: error instanceof Error ? error.message : 'Upload failed' 
          } : u
        ));
      }
    }

    // Add successfully uploaded URLs to photos
    if (uploadedUrls.length > 0) {
      onPhotosChange([...photos, ...uploadedUrls]);
      toast.success('Photos uploaded', `${uploadedUrls.length} photo(s) uploaded successfully`);
    }

    // Clear uploading files after a delay
    setTimeout(() => {
      setUploadingFiles([]);
    }, 2000);
  }, [photos, maxPhotos, onPhotosChange, toast, presignUpload, jobId]);

  const handleDelete = useCallback((index: number) => {
    const newPhotos = [...photos];
    newPhotos.splice(index, 1);
    onPhotosChange(newPhotos);
    toast.success('Photo removed', 'The photo has been removed from the list');
  }, [photos, onPhotosChange, toast]);

  const handleCameraClick = () => {
    cameraInputRef.current?.click();
  };

  const handleGalleryClick = () => {
    fileInputRef.current?.click();
  };

  const remainingSlots = maxPhotos - photos.length;

  return (
    <div className="space-y-4">
      {/* Photo Grid - Already uploaded */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((photo, index) => (
            <div key={index} className="relative aspect-square group">
              <DeliveryPhoto
                jobId={jobId}
                photoUrl={photo}
                alt={`Photo ${index + 1}`}
                className="w-full h-full rounded-lg border border-gray-200"
              />
              {/* Delete button */}
              <button
                onClick={() => handleDelete(index)}
                disabled={isUploading}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md hover:bg-red-600 transition-colors disabled:opacity-50 z-10"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Uploading Files Progress */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          {uploadingFiles.map((file) => (
            <div key={file.id} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-blue-700 truncate flex-1">{file.file.name}</span>
                <span className="text-blue-600 ml-2">
                  {file.status === 'uploading' && <Loader2 className="w-4 h-4 animate-spin" />}
                  {file.status === 'done' && 'Done'}
                  {file.status === 'error' && 'Failed'}
                </span>
              </div>
              {file.error && (
                <p className="text-red-500 text-xs mt-1">{file.error}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Photo Buttons */}
      {remainingSlots > 0 && !isUploading && (
        <div className="flex gap-3">
          <button
            onClick={handleCameraClick}
            className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-blue-700 active:bg-blue-800 transition-colors"
          >
            <Camera className="w-5 h-5" />
            Camera
          </button>
          <button
            onClick={handleGalleryClick}
            className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-gray-200 active:bg-gray-300 transition-colors"
          >
            <ImageIcon className="w-5 h-5" />
            Gallery
          </button>
        </div>
      )}

      {/* Uploading indicator */}
      {isUploading && (
        <div className="flex items-center justify-center gap-2 py-3 text-blue-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="font-medium">Uploading photos...</span>
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />

      {/* Count indicator */}
      <p className="text-sm text-gray-500 text-center">
        {photos.length} / {maxPhotos} photos
        {remainingSlots === 0 && (
          <span className="text-orange-600 block mt-1">Maximum photos reached</span>
        )}
      </p>
    </div>
  );
};
